import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Flex, Image, message, Select, Space, Typography } from 'antd';
import RejectDialog from './RejectDialog';

import './index.css';
import {
  approveArtwork,
  getArtworkDetail,
  rejectArtwork,
} from '@/api/contentReview';
import dayjs from 'dayjs';
import { Artwork } from '@/types/contentReview';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { FORMAT_TIME } from '@/types/common';
import { CONTENT_REVIEW_IDS_KEY, CONTENT_REVIEW_INDEX_KEY } from '.';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ArtworkVerifyStatus } from '@/api/artworkVerify';

const { Text } = Typography;

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      borderLeft: '4px solid #1890ff',
      paddingLeft: 8,
      marginBottom: 16,
      fontSize: 16,
      fontWeight: 'bold',
    }}
  >
    {children}
  </div>
);

interface ArtworkItemProps {
  artwork?: Artwork;
  showPass?: boolean;
  handleNext?: () => void;
}

const ArtworkItem = ({
  artwork,
  showPass = true,
  handleNext,
}: ArtworkItemProps) => {
  const isMobile = useAtomValue(isMobileAtom);

  const [actionLoading, setLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (!artwork?.tags) {
      return;
    }
    const tags = artwork.tags.split(',');
    setTagOptions(tags);
    setSelectedTags(tags);
  }, [artwork?.tags]);

  const labelStyle: React.CSSProperties = {
    fontWeight: 'bold',
    padding: isMobile ? 0 : '0 10px',
    width: 300,
    flexShrink: 0,
  };

  const handleTagsChange = (value: string[]) => {
    // 去除特殊字符，需要支持英文、中文、日文
    const validateRegex =
      /^[a-zA-Z0-9\u4e00-\u9fa5\u3040-\u309F\u30A0-\u30FF]+$/;
    if (value.some((tag) => !validateRegex.test(tag))) {
      messageApi.error('输入内容包含非法字符');
      return;
    }
    setSelectedTags(value);
  };

  const handleApprove = async () => {
    setLoading(true);
    const resp = await approveArtwork({
      artworkId: artwork?.artworkId || '',
      tags: selectedTags.join(','),
    });
    if (resp.status !== 200) {
      messageApi.error('Approve failed');
      return;
    }
    messageApi.success('Approve success');
    artwork!.reviewKey = ArtworkVerifyStatus.APPROVED;
    setLoading(false);
  };

  const handleReject = () => {
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async (
    image: number[],
    title: number[],
    description: number[],
    remark: string
  ) => {
    setLoading(true);
    const resp = await rejectArtwork({
      artworkId: artwork?.artworkId || '',
      reviewRemark: remark,
      imageRejectKeys: image.join(','),
      titleRejectKeys: title.join(','),
      descriptionRejectKeys: description.join(','),
    });
    if (resp.status !== 200) {
      messageApi.error('Reject failed');
      return;
    }
    messageApi.success('Reject success');
    artwork!.reviewKey = ArtworkVerifyStatus.REJECTED;
    setLoading(false);
  };


  const approveDisabled = useMemo(
    () =>
      !artwork ||
      artwork?.reviewKey === ArtworkVerifyStatus.APPROVED ||
      actionLoading,
    [artwork?.reviewKey, actionLoading]
  );
  const rejectDisabled = useMemo(
    () =>
      !artwork ||
      artwork?.reviewKey === ArtworkVerifyStatus.REJECTED ||
      actionLoading,
    [artwork?.reviewKey, actionLoading]
  );

  const renderImages = useMemo(() => {
    return artwork?.imageUrlList?.map((imageUrl, index) => (
      <Image
        width={isMobile ? 100 : 150}
        key={index}
        src={imageUrl}
        alt="artwork"
      />
    ));
  }, [artwork?.imageUrlList, isMobile]);

  // 高亮敏感词的函数
  const highlightBannedWords = useCallback((content: string, bannedWords: string | null) => {
    if (!bannedWords || !content) {
      return <span>{content}</span>;
    }

    const words = bannedWords.split(',').filter(Boolean);
    if (words.length === 0) {
      return <span>{content}</span>;
    }

    const parts = [];
    let lastIndex = 0;

    // 创建正则表达式匹配所有敏感词
    const pattern = new RegExp(words.join('|'), 'g');
    let match;

    while ((match = pattern.exec(content)) !== null) {
      // 添加非匹配部分
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {content.slice(lastIndex, match.index)}
          </span>
        );
      }
      // 添加高亮的匹配部分
      parts.push(
        <Text key={`highlight-${match.index}`} mark>
          {match[0]}
        </Text>
      );
      lastIndex = pattern.lastIndex;
    }

    // 添加剩余的文本
    if (lastIndex < content.length) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {content.slice(lastIndex)}
        </span>
      );
    }

    return <span>{parts}</span>;
  }, []);

  return (
    <div style={{ padding: '20px 0' }}>
      {contextHolder}
      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>基本信息</SectionTitle>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Artwork ID</div>
          <div>{artwork?.artworkId || '-'}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Publish Time</div>
          <div>{dayjs(artwork?.reviewTime).format(FORMAT_TIME) || '-'}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Latest Review</div>
          <div>{artwork?.reviewUser || '-'}</div>
        </Flex>
      </Card>

      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>用户信息</SectionTitle>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>SUPP ID</div>
          <div>{artwork?.suppId || '-'}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>用户名</div>
          <div>{artwork?.userName || '-'}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>用户类型</div>
          <div>{artwork?.isArtist ? '艺术家' : '普通用户'}</div>
        </Flex>
      </Card>

      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>内容信息</SectionTitle>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Artwork Title</div>
          <div>{highlightBannedWords(artwork?.title || '-', artwork?.bannedWords!)}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Artwork Descriptions</div>
          <div style={{ maxWidth: 500, wordWrap: 'break-word' }}>
            {highlightBannedWords(artwork?.description || '-', artwork?.bannedWords!)}
          </div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Tags</div>
          <Select
            mode="tags"
            style={{ width: 300 }}
            value={selectedTags}
            options={tagOptions.map((tag) => ({ label: tag, value: tag }))}
            onChange={handleTagsChange}
          />
        </Flex>
      </Card>

      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>敏感信息</SectionTitle>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>敏感词</div>
          <div>
            {artwork?.bannedWords ? (
              <Text type="danger">{artwork.bannedWords}</Text>
            ) : (
              '无'
            )}
          </div>
        </Flex>
        {artwork?.bannedImageFlag && (
          <Flex
            style={{
              marginBottom: 10,
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <div style={labelStyle}>敏感图片</div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {artwork.bannedImageList?.map((image, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  <Image
                    width={isMobile ? 100 : 150}
                    src={image.imageUrl}
                    alt={`敏感图片 ${index + 1}`}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Text type="danger">分类: {image.classify}</Text>
                  </div>
                </div>
              ))}
            </div>
          </Flex>
        )}
      </Card>

      <Card>
        <SectionTitle>作品图片</SectionTitle>
        <Flex
          style={{
            flexWrap: 'nowrap',
            overflowX: 'auto',
            gap: '16px',
            padding: '8px 0',
          }}
        >
          <Image.PreviewGroup>{renderImages}</Image.PreviewGroup>
        </Flex>
      </Card>

      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Space>
          <Button type="primary" onClick={handleApprove} disabled={approveDisabled}>
            Approve
          </Button>
          <Button
            type="primary"
            danger
            onClick={handleReject}
            disabled={rejectDisabled}
          >
            Reject
          </Button>
          {showPass && <Button onClick={handleNext}>Pass</Button>}
        </Space>
      </div>

      <RejectDialog
        isOpen={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
      />
    </div>
  );
};

const DetailPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentArtworkId, setCurrentArtworkId] = useState<string>('');
  const idsRef = useRef<string[]>([]);

  const { data: currentArtwork } = useQuery({
    queryKey: ['currentArtwork', currentArtworkId],
    queryFn: async () => {
      const resp = await getArtworkDetail(currentArtworkId);
      return resp.data;
    },
    enabled: !!currentArtworkId,
  });

  useEffect(() => {
    const index = parseInt(searchParams.get(CONTENT_REVIEW_INDEX_KEY) || '0');
    const ids = searchParams.get(CONTENT_REVIEW_IDS_KEY)?.split(',') || [];
    idsRef.current = ids;
    if (ids.length) {
      setCurrentIndex(index);
      setCurrentArtworkId(ids[index]);
    }
  }, [searchParams]);

  const handlePrev = useCallback(() => {
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    const params = new URLSearchParams(searchParams);
    params.set(CONTENT_REVIEW_INDEX_KEY, newIndex.toString());
    setSearchParams(params);
    const prevId = idsRef.current[newIndex];
    if (prevId) {
      setCurrentArtworkId(prevId);
    }
  }, [currentIndex, searchParams, setSearchParams]);

  const handleNext = useCallback(() => {
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    const params = new URLSearchParams(searchParams);
    params.set(CONTENT_REVIEW_INDEX_KEY, newIndex.toString());
    setSearchParams(params);
    const nextId = idsRef.current[newIndex];
    if (nextId) {
      setCurrentArtworkId(nextId);
    }
  }, [currentIndex, searchParams, setSearchParams]);

  return (
    <div className="content-review-detail-container">
      <div className="pagination-btn-container">
        <Button
          type="primary"
          shape="circle"
          onClick={handlePrev}
          disabled={currentIndex === 0}
          className="pagination-btn-left"
        >
          &lt;
        </Button>
      </div>

      <div className="artwork-item-container">
        <ArtworkItem
          artwork={currentArtwork}
          handleNext={handleNext}
          showPass={currentIndex !== idsRef.current.length - 1}
        />
      </div>

      <div className="pagination-btn-container">
        <Button
          type="primary"
          shape="circle"
          onClick={handleNext}
          disabled={currentIndex === idsRef.current.length - 1}
          className="pagination-btn-right"
        >
          &gt;
        </Button>
      </div>
    </div>
  );
};

export default DetailPage;
